import React, { useState } from "react";

import MuiCard from "@mui/material/Card";
import FormControlLabel from "@mui/material/FormControlLabel";
import Modal from "@mui/material/Modal";
import styled from "@mui/material/styles/styled";
import Typography from "@mui/material/Typography";

import useAuth from "@app/common/auth/useAuth";
import useUserMetadata from "@app/common/auth/useUserMetadata";
import { toast } from "@app/common/hooks/useToasts";
import LINKS from "@app/common/links";
import * as PolicyUtils from "@app/common/utils/policyUtils";
import Config from "@app/config";

import Button from "@rui/Button";
import Checkbox from "@rui/Checkbox";
import Link from "@rui/Link";
import { Column, Row } from "@rui/Stack";
import { Bold } from "@rui/styles";

const Card = styled(MuiCard)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "500px",
  maxWidth: "95%",
  maxHeight: "95%",
  padding: theme.spacing(3, 3, 2),
}));

const CONTENT_BY_REASON = {
  updatedPolicy: {
    title: "Updated Data Processing Agreement",
    getContent: () =>
      "We've updated our data processing agreement. As a Workspace owner, you must read and agree to the updated terms to continue using Read.",
  },
  workspaceTransfer: {
    title: "Data Processing Agreement",
    getContent: ({ workspaceName }: { workspaceName?: string }) => (
      <>
        You were recently transferred ownership of{" "}
        {workspaceName ? (
          <>
            the <Bold>{workspaceName}</Bold>{" "}
          </>
        ) : (
          "your"
        )}
        Workspace. As a Workspace owner, you must read and agree to the terms to
        continue using Read. For more information, reach out to{" "}
        <Link href={LINKS.EMAIL.SUPPORT} fontWeight={400}>
          support@read.ai
        </Link>
        .
      </>
    ),
  },
};

const UpdatedDataProcessingAgreementDialog = () => {
  const { user, impersonating } = useAuth();
  const { userMetadata, updateUserMetadata } = useUserMetadata();

  const [accepted, setAccepted] = useState(false);

  const handleToggleAccept = (
    event: React.SyntheticEvent,
    checked: boolean
  ) => {
    setAccepted(checked);
  };

  const handleContinue = () => {
    updateUserMetadata.mutate(
      {
        lastAcceptedDataProcessingAgreementVersion:
          Config.policyVersions.dataProcessingAgreement,
        requireDataProcessingAgreementAcceptReason: null,
      },
      {
        onError: () => {
          toast.error(
            "Something went wrong. Please refresh the page or contact support@read.ai if the problem persists."
          );
        },
      }
    );
  };

  // don't show this dialog while impersonating; only the user can accept the agreement
  const showDialog =
    !impersonating && PolicyUtils.requireAcceptDataProcessingAgreement(user);

  if (!showDialog) return null;

  const { title, getContent } =
    userMetadata?.requireDataProcessingAgreementAcceptReason ===
    "workspace_transfer"
      ? CONTENT_BY_REASON.workspaceTransfer
      : CONTENT_BY_REASON.updatedPolicy;

  return (
    <Modal open disableAutoFocus>
      <Card>
        <Column spacing={2}>
          <Typography variant="h2" color="shades.neutral.700">
            {title}
          </Typography>
          <Column spacing={1}>
            <Typography variant="bodyPrimaryReg" color="shades.neutral.600">
              {getContent({ workspaceName: user?.workspace?.name })}
            </Typography>
            <FormControlLabel
              control={<Checkbox variant="filled" />}
              onChange={handleToggleAccept}
              label={
                <Typography
                  variant="bodySecondaryReg"
                  color="shades.neutral.600"
                >
                  I have read and agree to the{" "}
                  <Link href={LINKS.MARKETING.DATA_PROCESSING_ADDENDUM}>
                    Data Processing Agreement
                  </Link>
                </Typography>
              }
            />
          </Column>
        </Column>
        <Row variant="right">
          <Button
            onClick={handleContinue}
            disabled={!accepted}
            loading={updateUserMetadata.isPending}
          >
            Continue
          </Button>
        </Row>
      </Card>
    </Modal>
  );
};

export default UpdatedDataProcessingAgreementDialog;
